/**
 * Analytics Adapter Interface
 * All adapters must implement this interface
 */

import type {
  PageViewEvent,
  TrackEvent,
  IdentifyEvent,
  ErrorEvent,
  PerformanceEvent,
  AdapterConfig,
} from '../../domain/types'

/**
 * Analytics adapter interface
 * Defines the contract for all analytics providers
 */
export interface AnalyticsAdapter {
  /** Adapter name for identification */
  readonly name: string

  /** Initialize the adapter */
  initialize(config: AdapterConfig): Promise<void>

  /** Track a page view */
  trackPageView(event: PageViewEvent): void

  /** Track a custom event */
  track(event: TrackEvent): void

  /** Identify a user */
  identify(event: IdentifyEvent): void

  /** Report an error */
  trackError(event: ErrorEvent): void

  /** Report a performance metric */
  trackPerformance(event: PerformanceEvent): void

  /** Reset/clear user identity (on logout) */
  reset(): void

  /** Flush any pending events */
  flush(): Promise<void>

  /** Check if adapter is ready */
  isReady(): boolean

  /** Set user consent state */
  setConsent(granted: boolean): void
}

/**
 * Abstract base class for analytics adapters
 * Provides common functionality
 */
export abstract class BaseAnalyticsAdapter implements AnalyticsAdapter {
  abstract readonly name: string

  protected config: AdapterConfig | null = null
  protected isInitialized = false
  protected hasConsent = false
  protected eventQueue: Array<
    PageViewEvent | TrackEvent | IdentifyEvent | ErrorEvent | PerformanceEvent
  > = []

  async initialize(config: AdapterConfig): Promise<void> {
    this.config = config
    this.isInitialized = true
  }

  abstract trackPageView(event: PageViewEvent): void
  abstract track(event: TrackEvent): void
  abstract identify(event: IdentifyEvent): void
  abstract trackError(event: ErrorEvent): void
  abstract trackPerformance(event: PerformanceEvent): void

  reset(): void {
    // Override in subclass if needed
  }

  async flush(): Promise<void> {
    // Override in subclass if needed
    this.eventQueue = []
  }

  isReady(): boolean {
    return this.isInitialized
  }

  setConsent(granted: boolean): void {
    this.hasConsent = granted

    // Process queued events if consent granted
    if (granted && this.eventQueue.length > 0) {
      this.processQueue()
    }
  }

  protected processQueue(): void {
    for (const event of this.eventQueue) {
      switch (event.type) {
        case 'page_view':
          this.trackPageView(event)
          break
        case 'track':
          this.track(event)
          break
        case 'identify':
          this.identify(event)
          break
        case 'error':
          this.trackError(event)
          break
        case 'performance':
          this.trackPerformance(event)
          break
      }
    }
    this.eventQueue = []
  }

  protected shouldTrack(): boolean {
    if (!this.isInitialized) return false
    return this.hasConsent
  }

  protected shouldTrackError(): boolean {
    // Errors should always be tracked if initialized
    return this.isInitialized
  }
}
